/** The one place a render error is caught.
 *
 * React unmounts the whole tree when a component throws, and what the visitor
 * is left with is the document's background colour. This sits inside the
 * shell, around whichever screen is routed, so a throw costs that screen and
 * not the header — the links out of trouble stay where they were.
 *
 * It is a class because an error boundary still has to be one. `Shell` keys
 * it by address, so following one of those links mounts a fresh boundary and
 * the red flag does not follow the visitor to a page that works.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Crashed } from '@/components/ui/Crashed';

interface State {
  error: unknown;
  at: string;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, at: '' };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error ?? new Error('unknown error'), at: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z') };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // For whoever opens the console after the screenshot arrives.
    console.error('[portal] a screen failed to render', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) return <Crashed error={this.state.error} at={this.state.at} />;
    return this.props.children;
  }
}

export default ErrorBoundary;
